import { GetCruise, GetDay, GetTrip } from "../../App/Context"
import { Icons, IconType } from "../../Components/Icon"
import { ApiClient, DayApi, ICruise, IDay, IDayResponse, INote, INoteAttachmentResponse, INoteCreateRequest, INoteParentType, INoteResponse, INoteType, IShipAutocompleteResponse, ITrip, NoteApi, ShipApi } from "../../Types"
import { NoteParentTypeConfigurations, NoteTypeIcons } from "./Params"

const noteIcons: NoteTypeIcons = {
  [INoteType.planning]: Icons.NoteCruisePlan,
  [INoteType.journal]: Icons.NoteJournal,
  [INoteType.technical]: Icons.NoteTechnical,
  [INoteType.weather]: Icons.NoteWeatherReport,
};

export function NoteTypeIcon(noteType: INoteType): IconType {
  return noteIcons[noteType] || Icons.Note;
}

export function NoteParent(parentType: INoteParentType, cruise?: ICruise, day?: IDay, trip?: ITrip): ICruise | IDay | ITrip | undefined {
  switch (parentType) {
    case INoteParentType.cruise:
      return cruise;
    case INoteParentType.day:
      return day;
    case INoteParentType.trip:
      return trip;
    default:
      return undefined;
  }
}

export function NoteOwnedByParent(parentType: INoteParentType, note?: INote): boolean {
  if (!note) {
    return false;
  }
  switch (parentType) {
    case INoteParentType.cruise:
      return note.tripId == undefined && note.dayId == undefined;
    case INoteParentType.day:
      return note.dayId != undefined;
    case INoteParentType.trip:
      return note.tripId != undefined && note.dayId == undefined;
    default:
      return false;
  }
}

export const NoteParentTypeConfiguration: NoteParentTypeConfigurations = {
  [INoteParentType.cruise]: {
    types: [INoteType.planning],
  },
  [INoteParentType.day]: {
    types: [INoteType.weather, INoteType.journal, INoteType.technical],
  },
  [INoteParentType.trip]: {
    types: [INoteType.journal, INoteType.technical],
  },

};

export function GetNoteParent(parentType: INoteParentType): [ICruise | undefined, ITrip | undefined, IDay | undefined] {
  // Read every context unconditionally so the hook call order stays stable
  // regardless of parentType. Branching the `useContext` calls per parentType
  // (as before) changed the hook order across renders, which React flagged with
  // "change in the order of Hooks called by NoteView".
  const cruise = GetCruise()?.cruise;
  const trip = GetTrip()?.trip;
  const day = GetDay()?.day;
  switch (parentType) {
    case INoteParentType.cruise:
      return [cruise, undefined, undefined];
    case INoteParentType.day:
      return [undefined, undefined, day];
    case INoteParentType.trip:
      return [undefined, trip, undefined];
    default:
      return [undefined, undefined, undefined];
  }
}

export const singleNoteTypes = [INoteType.weather];
export const autoCreateNoteTypes = [INoteType.journal, INoteType.technical, INoteType.weather];
export const dateFieldNoteTypes = [INoteType.weather, INoteType.planning];
export const noteInputNoteTypes = [INoteType.planning];
export const noteExtraNoteTypes = [INoteType.weather];

// Which content fields a note type exposes. This is the single source of truth for the
// note-type -> field mapping, derived from the arrays above and shared by the interactive
// NoteView form and the Print note renderers so the two cannot disagree on, e.g., which
// note types carry a noteExtra field.
export interface NoteContentFields {
  hasDate: boolean;
  noteIsInput: boolean;
  hasNoteExtra: boolean;
}

export const NoteViewData = {
  noteContentFields(noteType: INoteType): NoteContentFields {
    return {
      hasDate: dateFieldNoteTypes.indexOf(noteType) >= 0,
      noteIsInput: noteInputNoteTypes.indexOf(noteType) >= 0,
      hasNoteExtra: noteExtraNoteTypes.indexOf(noteType) >= 0,
    };
  },
  loadWeatherNoteTitleAutocomplete(api: ApiClient, shipId: number): Promise<IShipAutocompleteResponse> {
    return new ShipApi(api).ships.autocomplete({ params: { shipId, field: "weatherNoteTitle" } }).call();
  },
  // #1137: creates the Day row a blank/backfilled day's draft note is the first thing
  // to touch -- idempotent server-side (a duplicate create for the same date returns
  // the existing row), so a second note-type tab's own draft save races safely.
  createDay(api: ApiClient, shipId: number, tripId: number, day: number): Promise<IDayResponse> {
    return new DayApi(api).days.create({ params: { shipId, tripId }, body: { day } }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  saveNote(api: ApiClient, shipId: number, noteId: number, values: any): Promise<INoteResponse> {
    return new NoteApi(api).notes.update({ params: { shipId, noteId }, body: values }).call();
  },
  createNote(api: ApiClient, parentType: INoteParentType, shipId: number, parentId: number, body: INoteCreateRequest): Promise<INoteResponse> {
    const notes = new NoteApi(api).notes;
    switch (parentType) {
      case INoteParentType.cruise:
        return notes.createForCruise({ params: { shipId, cruiseId: parentId }, body }).call();
      case INoteParentType.day:
        return notes.createForDay({ params: { shipId, dayId: parentId }, body }).call();
      case INoteParentType.trip:
        return notes.createForTrip({ params: { shipId, tripId: parentId }, body }).call();
      default:
        throw new Error(`Unknown note parent type: ${parentType}`);
    }
  },
  uploadAttachment(api: ApiClient, shipId: number, noteId: number, attachment: File): Promise<INoteAttachmentResponse> {
    return new NoteApi(api).noteAttachments.create({ params: { shipId, noteId }, body: { attachment } }).call();
  },
};
